import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  IconButton,
  useTheme,
  Chip,
  TableFooter,
} from "@mui/material";
import {
  Search,
  Close,
  StorefrontOutlined,
  CheckCircleOutline,
  HelpOutline,
} from "@mui/icons-material";
import formatRupiah from "utils/formatRupiah";

const DetailSetoranCabangModal = ({
  open,
  onClose,
  data = [],
  loading = false,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const lower = searchQuery.toLowerCase();
    return data.filter(
      (item) =>
        (item.nama_cab || "").toLowerCase().includes(lower) ||
        (item.nama_karyawan || "").toLowerCase().includes(lower),
    );
  }, [data, searchQuery]);

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, curr) => {
        const cashVal =
          Number(curr.nominal_setoran) === Number(curr.transfer)
            ? 0
            : Number(curr.nominal_setoran) || 0;
        acc.totalCash += Number(cashVal);
        acc.totalTransfer += Number(curr.transfer || 0);
        acc.totalEdc += Number(curr.total_debit_credit || 0);
        acc.totalQris += Number(curr.total_ewallet || 0);
        return acc;
      },
      { totalCash: 0, totalTransfer: 0, totalEdc: 0, totalQris: 0 },
    );
  }, [filteredData]);

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #1e1e1e, #2a2a2a)"
              : "#ffffff",
          boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          pb: 1,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight="800"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.secondary[200]}, ${theme.palette.secondary[500]})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "0.5px",
            }}
          >
            Setoran Per Cabang
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={10}
            gap={2}
          >
            <CircularProgress color="secondary" size={50} />
            <Typography variant="body1" color="text.secondary">
              Sedang mengambil data setoran per cabang...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ animation: "fadeIn 0.3s ease" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              flexWrap="wrap"
              gap={2}
            >
              <TextField
                placeholder="Cari nama cabang atau penyetor..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        sx={{ color: "text.secondary", fontSize: "20px" }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                      >
                        <Close sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 320 },
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.05)"
                      : "#ffffff",
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight="500"
              >
                Menampilkan <strong>{filteredData.length}</strong> dari{" "}
                <strong>{data.length}</strong> cabang
              </Typography>
            </Box>

            {/* Table */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: 500,
                borderRadius: "16px",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)"
                }`,
                backgroundColor: "transparent",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "800",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#222" : "#f5f5f5",
                      }}
                    >
                      Nama Cabang
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "800",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#222" : "#f5f5f5",
                      }}
                    >
                      Penyetor
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "800",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#222" : "#f5f5f5",
                      }}
                    >
                      Cash (Tunai)
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "800",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#222" : "#f5f5f5",
                      }}
                    >
                      Transfer
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "800",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#222" : "#f5f5f5",
                      }}
                    >
                      EDC
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "800",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#222" : "#f5f5f5",
                      }}
                    >
                      QRIS
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: "800",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#222" : "#f5f5f5",
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          Tidak ada data setoran cabang yang cocok.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((item, idx) => {
                      const isApproved = item.status === "disetujui";
                      return (
                        <TableRow
                          key={item.id || idx}
                          sx={{
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.02)"
                                  : "rgba(0,0,0,0.01)",
                            },
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <StorefrontOutlined
                                sx={{ color: theme.palette.secondary.main }}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight="700">
                                  {item.nama_cab || `-`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  ID: {item.id_cab || `-`}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {item.nama_karyawan || `-`}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "600", color: "#9c27b0" }}
                          >
                            {formatRupiah(
                              Number(item.nominal_setoran) ===
                                Number(item.transfer)
                                ? 0
                                : item.nominal_setoran || 0,
                            )}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "600", color: "#1976d2" }}
                          >
                            {formatRupiah(item.transfer || 0)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "600", color: "#2e7d32" }}
                          >
                            {formatRupiah(item.total_debit_credit || 0)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "600", color: "#ed6c02" }}
                          >
                            {formatRupiah(item.total_ewallet || 0)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              icon={
                                isApproved ? (
                                  <CheckCircleOutline
                                    sx={{ fontSize: "16px !important" }}
                                  />
                                ) : (
                                  <HelpOutline
                                    sx={{ fontSize: "16px !important" }}
                                  />
                                )
                              }
                              label={
                                isApproved ? "Disetujui" : "Belum Disetujui"
                              }
                              size="small"
                              color={isApproved ? "success" : "warning"}
                              sx={{
                                fontWeight: "600",
                                borderRadius: "8px",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
                {filteredData.length > 0 && (
                  <TableFooter
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#222" : "#f5f5f5",
                      zIndex: 2,
                      boxShadow: "0 -2px 5px rgba(0,0,0,0.05)",
                    }}
                  >
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          fontWeight: "800",
                          fontSize: "13px",
                          color: theme.palette.text.primary,
                        }}
                      >
                        Total Keseluruhan
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: "800",
                          fontSize: "13px",
                          color: "#9c27b0",
                        }}
                      >
                        {formatRupiah(totals.totalCash)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: "800",
                          fontSize: "13px",
                          color: "#1976d2",
                        }}
                      >
                        {formatRupiah(totals.totalTransfer)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: "800",
                          fontSize: "13px",
                          color: "#2e7d32",
                        }}
                      >
                        {formatRupiah(totals.totalEdc)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: "800",
                          fontSize: "13px",
                          color: "#ed6c02",
                        }}
                      >
                        {formatRupiah(totals.totalQris)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="inherit"
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: "600",
            px: 4,
          }}
        >
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetailSetoranCabangModal;
